import React from "react";

import axios from "axios";
import { Grid } from "@material-ui/core";
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DoneIcon from '@mui/icons-material/Done';
import UploadIcon from '@mui/icons-material/Upload';
import TextField from '@mui/material/TextField';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CancelIcon from '@mui/icons-material/Cancel';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import esLocale from "date-fns/locale/es";

import ImgPlaceHolder from "../../../assets/image-place-holder.svg";
import * as Constants from "../../../consts/Constants.js";

import { Advertisement } from '../../../plas/services/ads/models';
import service from '../../../plas/services/ads/service';
import placesService from '../../../plas/map/places/service';
import alerter from "../../alerts/alerter"
import { createByteArray } from "../../../plas/utils";

export default function AdvertisementForm({
	initialAdvertisement,
	initialPlaces,
	placeCategory,
	onCancel,
	onSave,
	fullWidth
}) {

	const theme = createTheme({
	  palette: {
	    primary: {
	      main: '#FFFFFF',
	      darker: '#FFFFFF',
	    }
	  },
	});

	const [advertisement, setAdvertisement] = React.useState(initialAdvertisement ? initialAdvertisement : new Advertisement());

	const [places, setPlaces] = React.useState([]);
	const [loadingPlaces, setLoadingPlaces] = React.useState(false);

	const [selectedPlace, setSelectedPlace] = React.useState(null);
	const [title, setTitle] = React.useState(initialAdvertisement ? initialAdvertisement.title : null);
	const [body, setBody] = React.useState(initialAdvertisement ? initialAdvertisement.body : null);
  	const [startDate, setStartDate] = React.useState(initialAdvertisement ? initialAdvertisement.startDate : null);
  	const [endDate, setEndDate] = React.useState(initialAdvertisement ? initialAdvertisement.endDate : null);
	const [url, setUrl] = React.useState(initialAdvertisement? initialAdvertisement.url : null);

	const inputFile = React.useRef(null)
	const [imageBlob, setImageBlob] = React.useState(null);

	const [saving, setSaving] = React.useState(false);

	React.useEffect(() => {
		if (!initialPlaces) {
			loadPlaces(placeCategory);
		} else {
			preparePlaces(initialPlaces)
			setLoadingPlaces(false);
		}

		if (advertisement.binId) {
			loadImage()
		}
	}, []);

	const preparePlaces = (listOfPlaces) => {
		var defaultValue = null;
		listOfPlaces.forEach((item) => {
			item.label = item.name + " (#" + item.id +")";
			if (item.id === advertisement.placeId)
				defaultValue = item;
		});
		setPlaces(listOfPlaces);
		setSelectedPlace(defaultValue)
	}


	const loadPlaces = (placeCategory) => {
		setLoadingPlaces(true);
		placesService.api.places.getPage(0, 2147483646, placeCategory, placeCategory ? false : undefined, null, (response) => {
	        if (response.error != null) {
        		alerter.showError(response.error);
        		if (onCancel)
        			onCancel()
			} else {
				preparePlaces(response.result.data);
				setLoadingPlaces(false);
			}
    	});
	}

	const saveHandler = () => {
		if (advertisement.id) {	
			update()
		} else {
			create();
		}
	}

	const loadImage = () => {
	    axios.get(service.api.advertisements.getMediaUrl(advertisement.id, advertisement.binId), { responseType: 'blob' }).then((response) => {
	        let blob = response.data;
	        let newBlobUrl = URL.createObjectURL(blob);
	        setImageBlob(newBlobUrl);
	      }
	    );
	}

  	const create = () => {
  		setSaving(true)
  		service.api.advertisements.create(advertisement, (response) => {
  			if (response.result != null) {
  				if (onSave)
  					onSave(response.result);
  			} else {
  				alerter.showError(response.error);
  			}
			setSaving(false)
		});
	}

	const update = () => {
  		setSaving(true)
  		service.api.advertisements.update(advertisement, (response) => {
  			if (response.result != null) {
  				if (onSave)
  					onSave(response.result);
  			} else {
  				alerter.showError(response.error);
  			}
			setSaving(false)
		});
	}

	const selectPlace = (newPlace) => {
		if (newPlace) {
			advertisement.placeId = newPlace.id;
			advertisement.place = newPlace;
			setSelectedPlace(newPlace);
		} else {
			advertisement.placeId = null;
			advertisement.place = null;
			setSelectedPlace(null);
		}
	}

	const imageButtonClick = () => {
	    inputFile.current.click();
	}

  	const onSelectFile = (event) => {
	    event.stopPropagation();
	    event.preventDefault();

	    var file = event.target.files[0];

	    if (file) {
			let url = URL.createObjectURL(file);
			advertisement.attachedBin = {
					name: file.name,
					type: file.type,
					data: createByteArray(url)
			}
			setImageBlob(url);
	    }
	}

	const removeImage = () => {
		advertisement.attachedBin = null;
		advertisement.binId = null;
		advertisement.bin = null;
		setImageBlob(null);
		setAdvertisement(new Advertisement({...advertisement}));
  	}

	const onChangeBody = (e) => {
		let value = e.target.value.trim();
		advertisement.body = value ? value : null;
		setBody(e.target.value);
	}

	const onChangeTitle = (e) => {
		let value = e.target.value.trim();
		advertisement.title = value ? value : null;
		setTitle(e.target.value);
	}

	const onChangeUrl = (e) => {
		let value = e.target.value.trim();
		advertisement.url = value ? value : null;
		setUrl(e.target.value);
	}

	const ColoredLine = ({ color }) => (
	    <hr
	        style={{
	            color: color,
	            backgroundColor: color,
	            marginTop: 6,
	            marginBottom: 16,
	            height: 2
	        }}
	    />
	);

	return(
 		<Grid container>
 			<Grid item xs={12} style={{ textAlign: "center"}}>
	   		{ loadingPlaces ?
	        (
	            <CircularProgress style={{color:Constants.COLOR_PRIMARY}} color="inherit" />
	        ) :
	        (<Grid container spacing={2}>
		 		<Grid item xs={false} md={ fullWidth ? false : 2} />
		       	<Grid item xs={12} md={ fullWidth ? 12 : 8} >
		       		<Grid container spacing={2}>
		       			<Grid item xs={12}>
							<p style={{ fontSize: '28px', textAlign: 'left'}}>
								Datos del evento
							</p>
							<ColoredLine color="gray"/>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={8}>
				       				<Grid container spacing={2}>
										<Grid item xs={12}>
											<Autocomplete
												disabled={!places || saving}
												options={places? places : []}
												renderInput={(params) => 
													<TextField 
														{...params}
														required  
														label="Lugar" 
													/>}
												value={selectedPlace}
												onChange={ (event, newValue) => selectPlace(newValue)}
											/>
										</Grid>
										<LocalizationProvider 
							        		adapterLocale={esLocale}
											dateAdapter={AdapterDateFns}>
											<Grid item xs={6}>
												<DatePicker
													disabled={saving}
											        label="Fecha de inicio"
											        value={startDate}
											        onChange={(newValue) => {
											        	if (newValue != null) {
												        	advertisement.startDate = newValue.getTime();
										                	setStartDate(newValue);
										                	if (endDate == null) {
																advertisement.endDate = newValue.getTime();
										                		setEndDate(newValue);
										                	}
									                	} else {
												        	advertisement.startDate = null;
															setStartDate(null);
									                	}
											        }}
											        renderInput={(params) => <TextField required fullWidth variant="outlined" {...params}/>}>
											    </DatePicker>
								            </Grid>
								            <Grid item xs={6}>
												<DatePicker
													disabled={saving}
											        label="Fecha de fin"
											        value={endDate}
											        onChange={(newValue) => {
											        	if (newValue != null) {
															advertisement.endDate = newValue.getTime();
										                	setEndDate(newValue);
										                	if (startDate == null) {
										                		advertisement.startDate = newValue.getTime();
										                		setStartDate(newValue);
										                	}
										                } else {
												        	advertisement.endDate = null;
															setEndDate(null);
									                	}
											        }}
											        renderInput={(params) => <TextField required fullWidth variant="outlined" {...params} />}>
											    </DatePicker>
							            	</Grid>
							        	</LocalizationProvider>
										<Grid item xs={12}>
											<TextField
												required
												variant="outlined"
												id="name"
												label="Titulo"
												name="title"
												onChange={onChangeTitle}
												disabled={saving}
												value={title}
												fullWidth
												placeholder="Titulo"/>
										</Grid>
										<Grid item xs={12}>
											<TextField
												variant="outlined"
												id="body"
												required
												disabled={saving}
												label="Descripción"
												name="body"
												multiline
												minRows={3}
												maxRows={6}
												value={body}
												onChange={onChangeBody}
												fullWidth
												placeholder="Descripción del aviso o evento..."/>
										</Grid>
										<Grid item xs={12}>
											<TextField
												variant="outlined"
												id="url"
												disabled={saving}
												label="URL"
												name="url"
												value={url}
												onChange={onChangeUrl}
												fullWidth
												placeholder="Link a mas información..."/>
										</Grid>
					             	</Grid>
				       			</Grid>
				       			<Grid item xs={12} sm={4}>
									<Box
									  display="flex"
									  justifyContent="center"
									  alignItems="center"
									  height="100%">
										{ imageBlob == null ? (
											<div>
												{ !advertisement.binId ? (
													<div>
														<img src={ImgPlaceHolder} style={{ objectFit: 'contain'}} alt=""/>
														<ThemeProvider theme={theme}>
															<Button
																disabled={saving}
																startIcon={<UploadIcon/>}
																variant="contained"
																onClick={imageButtonClick}>
																<input type='file' accept="image/*" id='file' ref={inputFile} style={{display: 'none'}} onChange={onSelectFile}/>
																Cargar imagen
															</Button>
														</ThemeProvider>
													</div>
													):(
														<CircularProgress style={{color:Constants.COLOR_PRIMARY}} size={32} color="inherit" />
													)
												}
											</div>
										) : (
											<ImageListItem style={{ maxWidth: '90%' }} >
												<img src={imageBlob} style={{ width: 'auto', maxWidth: '100%', height: 'auto', maxHeight: '370px', objectFit: 'contain'}} alt=""/>
												<ImageListItemBar
													actionIcon={
														<IconButton disable={saving} sx={{ color: 'rgba(255, 255, 255, 1)' }} onClick={removeImage}>
															<DeleteOutlineIcon fontSize={"small"}/>
														</IconButton>
													}
												/>
											</ImageListItem>
										)}
									</Box>
								</Grid>
							</Grid>
						</Grid>
						{ onCancel || onSave ? (
							<Grid item xs={12}>
								<ColoredLine color="gray"/>
								<Grid container spacing={2}>
									<Grid item xs={true}/>
									<Grid item>
										{ onCancel? (
											<ThemeProvider theme={theme}>
												<Button 
									            	disabled={saving} 
									            	startIcon={<CancelIcon/>} 
									            	variant="contained" 
									            	onClick={() => onCancel()}>
									            	Cancelar
									            </Button>
									        </ThemeProvider>) : (null)
								        }
									</Grid>
									<Grid item>
										{ onSave ? (
											<LoadingButton
												disabled={!advertisement.isValid()}
									            loadingPosition="start"
									            startIcon={<DoneIcon />}
									            loading={saving}
									            variant="contained"
									            onClick={saveHandler}>
									            <b>{ advertisement.id ? "Guardar" : "Crear" }</b>
							          		</LoadingButton>) : (null)
						          		}
									</Grid>
								</Grid>
							</Grid>) : (null)
						}
		       		</Grid>
	           	</Grid>
	       		<Grid item xs={false} md={ fullWidth ? false : 2} />
	  		</Grid>
	  		)}
			</Grid>
		</Grid>
	)

}
